import { formatInr } from '@/utils/currency';
import type { MapMarker } from '@/utils/dashboardMap';
import { getMapMarkerKey } from '@/utils/dashboardMap';
import { sharePercent } from '@/utils/sharePercent';
import styles from '../../Dashboard.module.scss';

interface MapSidebarProps {
  markers: MapMarker[];
  activeKey: string | null;
  activeMarker: MapMarker | null;
  totalOrders: number;
  totalRevenue: number;
  onSelect: (marker: MapMarker) => void;
}

export function MapSidebar({
  markers,
  activeKey,
  activeMarker,
  totalOrders,
  totalRevenue,
  onSelect,
}: MapSidebarProps) {
  const orderShare = activeMarker ? sharePercent(activeMarker.orders, totalOrders) : 0;
  const revenueShare = activeMarker ? sharePercent(activeMarker.revenue, totalRevenue) : 0;

  return (
    <aside className={styles.mapSidebar}>
      <div className={styles.mapSidebarTitle}>Cities ranked by revenue</div>

      {activeMarker ? (
        <div className={styles.mapSidebarFeatured}>
          <span className={styles.mapSidebarFeaturedLabel}>Selected city</span>
          <strong>{activeMarker.city}</strong>
          {activeMarker.state ? (
            <span className={styles.mapSidebarState}>{activeMarker.state}</span>
          ) : null}
          <div className={styles.mapSidebarFeaturedStats}>
            <div>
              <span>Orders</span>
              <strong>{activeMarker.orders}</strong>
            </div>
            <div>
              <span>Revenue</span>
              <strong>{formatInr(activeMarker.revenue)}</strong>
            </div>
          </div>
          <div className={styles.mapSidebarShares}>
            <div className={styles.mapSidebarShareRow}>
              <span>Order share</span>
              <div className={styles.mapSidebarBarTrack}>
                <span className={styles.mapSidebarBarFill} style={{ width: `${orderShare}%` }} />
              </div>
              <span>{orderShare}%</span>
            </div>
            <div className={styles.mapSidebarShareRow}>
              <span>Revenue share</span>
              <div className={styles.mapSidebarBarTrack}>
                <span
                  className={`${styles.mapSidebarBarFill} ${styles.mapSidebarBarFillAccent}`}
                  style={{ width: `${revenueShare}%` }}
                />
              </div>
              <span>{revenueShare}%</span>
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.mapSidebarList}>
        {markers.map((marker, index) => {
          const markerKey = getMapMarkerKey(marker);
          const isActive = activeKey === markerKey;
          const share = sharePercent(marker.orders, totalOrders);

          return (
            <button
              key={markerKey}
              type="button"
              className={`${styles.mapSidebarItem} ${isActive ? styles.mapSidebarItemActive : ''}`}
              onClick={() => onSelect(marker)}
            >
              <span className={styles.mapSidebarRank}>{index + 1}</span>
              <span className={styles.mapSidebarInfo}>
                <strong>{marker.city}</strong>
                <span>
                  {marker.orders} orders · {formatInr(marker.revenue)}
                </span>
                <span className={styles.mapSidebarBarTrack}>
                  <span className={styles.mapSidebarBarFill} style={{ width: `${share}%` }} />
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
